// Una fila de la lista de la ventana «Santi» (propuesta §5.1): dos líneas, sin avatares ni oro. Línea 1: el título y,
// a la derecha, cuándo se movió por última vez («10:12», «ayer», «lun», «22 oct») — o, si vence en 2 días o menos, la
// fecha en naranja («Vence el jueves»; rojo si ya venció). Línea 2: lo que se espera (el «qué falta» del centro) y el
// punto blanco si Santi escribió desde la última vez que Pau lo abrió. En «Esperando a Santi», todo un tono más bajo.
// La fila de lo cerrado esta semana es la misma, con «Hecho por Santi» / «Cancelado por ti» en la línea 2.
import { Calendar } from 'lucide-react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { PuntoNuevo } from './piezas';
import { cuandoFila, nombreDe, venceFila, YO } from './tiempo';
import type { CerradoDTO, ContratoDTO } from './tipos';

const FILA = 'flex w-full flex-col gap-0.5 rounded-[10px] px-3 py-2.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-oficina-enlace/70';
const ACTIVA = 'bg-white/[0.07] ring-1 ring-inset ring-white/[0.08]';

export function FilaContrato({ c, activo, alAbrir, corto = false, saliendo = false }: { c: ContratoDTO; activo: boolean; alAbrir: () => void; corto?: boolean; saliendo?: boolean }) {
  const mia = c.lado === YO;
  const nuevo = c.nuevos > 0;
  const vence = venceFila(c.vence, Date.now(), corto);
  const cuando = cuandoFila(c.ultimoEn);
  return (
    <button
      type="button"
      onClick={alAbrir}
      aria-current={activo ? 'true' : undefined}
      aria-label={[c.titulo, c.queFalta, vence?.texto ?? cuando, nuevo ? t('oficinaV2.pelotas.lista.nuevo') : ''].filter(Boolean).join('. ')}
      title={c.titulo}
      className={cn(FILA, activo ? ACTIVA : 'hover:bg-white/[0.04]', saliendo && 'pointer-events-none animate-out fade-out-0 slide-out-to-left-2 fill-mode-forwards duration-motion-default')}
      tabIndex={saliendo ? -1 : undefined}
      data-testid="fila-contrato"
      data-tema={c.temaId}
      data-nuevo={nuevo ? 'true' : undefined}
      data-saliendo={saliendo ? 'true' : undefined}
    >
      <span className="flex w-full min-w-0 items-baseline gap-3">
        <span className={cn('min-w-0 flex-1 truncate text-[16px] font-semibold leading-[22px]', mia ? 'text-oficina-texto' : 'text-oficina-texto2')}>{c.titulo}</span>
        {vence ? (
          <span
            className={cn('inline-flex shrink-0 items-center gap-1.5 self-center whitespace-nowrap text-[14px] font-semibold leading-[22px]', vence.tono === 'error' ? 'text-oficina-error' : 'text-oficina-bloqueado')}
            data-testid="fila-vence"
            data-tono={vence.tono}
          >
            <Calendar className="h-[15px] w-[15px]" aria-hidden />
            {vence.texto}
          </span>
        ) : (
          <span className={cn('shrink-0 whitespace-nowrap text-[14px] leading-[22px] tabular-nums', nuevo ? 'font-semibold text-oficina-texto' : 'text-oficina-texto3')} data-testid="fila-cuando">
            {cuando}
          </span>
        )}
      </span>
      <span className="flex w-full min-w-0 items-center gap-3">
        <span className={cn('min-w-0 flex-1 truncate text-[15px] leading-5', mia ? 'text-oficina-texto2' : 'text-oficina-texto3')}>{c.queFalta}</span>
        {nuevo ? <PuntoNuevo /> : null}
      </span>
    </button>
  );
}

/** Una fila de «Hecho esta semana»: el título y cuándo, y quién lo cerró y cómo. */
export function FilaCerrado({ c, activo, alAbrir }: { c: CerradoDTO; activo: boolean; alAbrir: () => void }) {
  const quien = c.por === YO ? t('oficinaV2.pelotas.lista.ti') : nombreDe(c.por);
  const como = t(c.motivo === 'cancelado' ? 'oficinaV2.pelotas.lista.canceladoPor' : 'oficinaV2.pelotas.lista.hechoPor', { quien });
  return (
    <button
      type="button"
      onClick={alAbrir}
      aria-current={activo ? 'true' : undefined}
      title={c.titulo}
      className={cn(FILA, activo ? ACTIVA : 'hover:bg-white/[0.04]')}
      data-testid="fila-cerrado"
      data-tema={c.temaId}
    >
      <span className="flex w-full min-w-0 items-baseline gap-3">
        <span className="min-w-0 flex-1 truncate text-[16px] font-semibold leading-[22px] text-oficina-texto2">{c.titulo}</span>
        <span className="shrink-0 whitespace-nowrap text-[14px] leading-[22px] text-oficina-texto3 tabular-nums">{cuandoFila(c.cerradoEn)}</span>
      </span>
      <span className="min-w-0 truncate text-[15px] leading-5 text-oficina-texto3">{como}</span>
    </button>
  );
}
